import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, MapPin, CheckCircle2, XCircle } from 'lucide-react';
import axiosInstance from '@/config/axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface Visit {
  _id: string;
  date: string | Date;
  timeSlot: string;
  propertyId: string;
  buyerId: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed' | string;
  message?: string;
  property?: {
    title: string;
    location?: {
      address: string;
    };
  };
  buyer?: {
    name: string;
    email: string;
    phone?: string;
  };
}

export function CRMVisitsPage() {
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchVisits = async () => {
    try {
      const response = await axiosInstance.get('/api/visits');
      setVisits(response.data);
    } catch (error) {
      toast.error('Error al cargar visitas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisits();
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-display font-black uppercase text-ink italic">Órdenes de Visita</h1>
          <p className="text-serif italic text-ink/60 mt-2">Agenda y seguimiento de recorridos por propiedades.</p>
        </div>

        <div className="space-y-4">
          {loading ? (
            Array(4).fill(0).map((_, i) => (
              <div key={i} className="h-24 brutalist-border bg-ink/5 animate-pulse" />
            ))
          ) : visits.length > 0 ? (
            visits.map((visit) => (
              <div key={visit._id} className="brutalist-border bg-parchment p-6 flex flex-col md:flex-row justify-between items-center gap-6 group hover:bg-ink hover:text-parchment transition-colors">
                <div className="flex items-center gap-6 w-full md:w-auto">
                  <div className="w-16 h-16 bg-terracotta flex flex-col items-center justify-center text-parchment">
                    <span className="text-xl font-display font-black leading-none">{format(new Date(visit.date), 'dd')}</span>
                    <span className="text-[10px] font-black uppercase tracking-widest">{format(new Date(visit.date), 'MMM', { locale: es })}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-display font-black uppercase italic">
                      {visit.property?.title || `Propiedad #${visit.propertyId.slice(-6)}`}
                    </h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-xs font-serif italic opacity-60">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {visit.timeSlot}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {visit.property?.location?.address || 'Ver detalles'}</span>
                    </div>
                    {visit.message && (
                      <p className="mt-2 text-xs opacity-60 line-clamp-1">"{visit.message}"</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-8 w-full md:w-auto justify-between md:justify-end border-t md:border-t-0 pt-4 md:pt-0 border-ink/10 group-hover:border-parchment/10">
                  <div className="text-right hidden md:block">
                    <p className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">Comprador</p>
                    <p className="font-display font-black uppercase italic">{visit.buyer?.name || `Usuario #${visit.buyerId.slice(-6)}`}</p>
                    <p className="text-[9px] opacity-40">{visit.buyer?.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {visit.status === 'pending' ? (
                      <span className="bg-moss/10 text-moss px-3 py-1 text-[10px] font-black uppercase tracking-widest border border-moss/20">Pendiente</span>
                    ) : visit.status === 'confirmed' ? (
                      <span className="bg-blue-500/10 text-blue-500 px-3 py-1 text-[10px] font-black uppercase tracking-widest border border-blue-500/20">Confirmada</span>
                    ) : visit.status === 'completed' ? (
                      <span className="bg-terracotta text-parchment px-3 py-1 text-[10px] font-black uppercase tracking-widest">Completada</span>
                    ) : (
                      <span className="bg-ink/10 text-ink/40 px-3 py-1 text-[10px] font-black uppercase tracking-widest">Cancelada</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="py-20 text-center brutalist-border bg-ink/5 italic text-ink/40">
              No hay visitas programadas en tu agenda.
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
